import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ClipboardList, Pencil, Users } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { CenteredContentLayout } from '@/components/layout/CenteredContentLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useHistoryBack } from '@/hooks/useHistoryBack';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { cn } from '@/lib/utils';
import { formatCount, formatNumber } from '@/utils/number';
import { sortBySortOrder } from '@/utils/sort';
import { surveyDetailQueryOptions } from '../api/surveys.api';
import { SurveyAnswerForm } from '../components/SurveyAnswerForm';
import { SurveyDetailHeader } from '../components/SurveyDetailHeader';
import { SurveyDetailSkeleton } from '../components/SurveyDetailSkeleton';
import { SurveyQuestionPreview } from '../components/SurveyQuestionPreview';
import type { SurveyDetail } from '../types/survey.types';

const SURVEY_DETAILS_TITLE = 'Survey Details';
const COUNT_BADGE_CLASS = 'h-5 min-w-5 justify-center px-1.5 text-3xs font-semibold tabular-nums';

interface SurveyQuestionsSectionProps {
  survey: SurveyDetail;
}

function SurveyQuestionsSection({ survey }: SurveyQuestionsSectionProps) {
  const sortedQuestions = sortBySortOrder(survey.questions);

  if (sortedQuestions.length === 0) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No questions"
        description="This survey has no questions yet."
      />
    );
  }

  if (survey.status === 'active') return <SurveyAnswerForm survey={survey} />;

  return (
    <ol className="flex flex-col gap-3">
      {sortedQuestions.map((question, index) => (
        <li key={question.id}>
          <SurveyQuestionPreview question={question} index={index} />
        </li>
      ))}
    </ol>
  );
}

export function SurveyDetailPage() {
  const { surveyId } = useParams({ from: '/_auth/feedback/surveys/$surveyId/' });
  const navigate = useNavigate();
  const goBack = useHistoryBack(() => void navigate({ to: '/feedback/surveys' }));
  useRememberViewedRow('surveys', surveyId);
  const {
    data: survey,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(surveyDetailQueryOptions(surveyId));

  const responseCount = survey?.responseCount ?? 0;

  function renderContent() {
    if (isLoading) return <SurveyDetailSkeleton />;
    if (error) {
      return (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this survey"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      );
    }
    if (!survey) {
      return (
        <EmptyState
          icon={ClipboardList}
          title="Survey unavailable"
          description="This survey may have been deleted or the link is no longer valid."
          fill
        />
      );
    }
    return (
      <>
        <SurveyDetailHeader survey={survey} />
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Questions</h2>
            <span className="text-sm text-muted-foreground">{survey.questions.length} total</span>
          </div>
          <SurveyQuestionsSection survey={survey} />
        </section>
      </>
    );
  }

  return (
    <CenteredContentLayout
      contentWidth="wide"
      header={
        <PageHeader
          title={survey?.title ?? SURVEY_DETAILS_TITLE}
          leading={<BackButton onClick={goBack} />}
          actions={
            <>
              <Link
                to="/feedback/surveys/$surveyId/submissions"
                params={{ surveyId }}
                className={cn(buttonVariants({ variant: 'default' }), 'gap-2')}
                aria-label={`View ${formatCount(responseCount, 'submission')}`}
              >
                <Users className="size-4" aria-hidden />
                View Submissions
                {survey ? (
                  <Badge variant="secondary" className={COUNT_BADGE_CLASS}>
                    {formatNumber(responseCount)}
                  </Badge>
                ) : (
                  <Skeleton className="h-5 w-6 rounded-full" />
                )}
              </Link>
              <Link
                to="/feedback/surveys/$surveyId/edit"
                params={{ surveyId }}
                className={cn(buttonVariants({ variant: 'default' }), 'gap-2')}
              >
                <Pencil className="size-4" aria-hidden />
                Edit Survey
              </Link>
            </>
          }
        />
      }
    >
      {renderContent()}
    </CenteredContentLayout>
  );
}
